import { Avatar, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { RATE_ICON_SRC } from "../constants";
import type { MatchProfileDetail, MatchReaction } from "../../../services/matchesApi";
import type { DesignerBioImages, MatchRecord } from "../types";
import {
  formatDistance,
  getCompatibilityLabel,
  getCompatibilityRatingFromMatchScore,
  getCompatibilityStars,
  getCounterpartReaction,
  getCounterpartReactionBadge,
  getHeroImage,
  getLocationLabel,
  getMatchDisplayId,
  getProfileImage,
  getProfileSnapshot,
  getTopTags,
} from "../utils";
import { MatchScoreRing } from "./MatchScoreRing";
import { ReactionButtons } from "./ReactionButtons";
import { MatchStatusPill } from "./MatchStatusPill";

export const MatchMobileDetail = ({
  match,
  profile,
  profileImages,
  userAccountType,
  onClose,
  onViewProfile,
  onLearnMore,
  showLearnMore,
  showViewProfile,
  onReactionChange,
  disableInteractions = false,
}: {
  match: MatchRecord;
  profile: MatchProfileDetail | null;
  profileImages: DesignerBioImages | null;
  userAccountType?: string | null;
  onClose: () => void;
  onViewProfile: () => void;
  onLearnMore?: () => void;
  showLearnMore: boolean;
  showViewProfile: boolean;
  onReactionChange: (next: MatchReaction) => void;
  disableInteractions?: boolean;
}) => {
  const snapshot = getProfileSnapshot(match);
  const topTags = getTopTags(match, profile);
  const profileImage = getProfileImage(match, profileImages, profile);
  const heroImage = getHeroImage(
    match,
    profileImages,
    profile,
    profileImage,
    userAccountType ?? null
  );
  const compatibilityRating =
    getCompatibilityRatingFromMatchScore(match.matchScore) ??
    profile?.compatibilityRating ??
    null;
  const stars = getCompatibilityStars(compatibilityRating);
  const compatibilityLabel = getCompatibilityLabel(compatibilityRating);
  const isOnline = profile?.isOnline ?? match.isOnline ?? false;
  const locationLabel = profile?.location || getLocationLabel(match);
  const counterpartReactionBadge = getCounterpartReactionBadge(
    getCounterpartReaction(match)
  );
  const showMatchMade = Boolean(match.isMatchMade);
  const displayMatchId = getMatchDisplayId(match.matchId, userAccountType ?? null);

  return (
    <div className="matches-page matches-detail-page matches-detail-page-mobile">
      <div className="matches-mobile-detail-header">
        <span className="matches-mobile-detail-title">{displayMatchId}</span>
        <IconButton
          className="matches-mobile-detail-close"
          onClick={onClose}
          aria-label="Close match detail"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className="matches-mobile-detail-hero">
        {heroImage ? (
          <img src={heroImage} alt={`${displayMatchId} cover`} loading="lazy" />
        ) : (
          <div className="matches-mobile-detail-hero-placeholder" />
        )}
        <div className="matches-mobile-detail-avatar">
          <Avatar src={profileImage || undefined}>
            {(match.name ?? displayMatchId).charAt(0)}
          </Avatar>
          <span className={`status-dot ${isOnline ? "online" : "offline"}`} />
        </div>
      </div>
      <div className="matches-mobile-detail-body">
        <div className="matches-mobile-detail-meta-stack">
          <div className="matches-mobile-detail-meta">
            <span>
              <PlaceOutlinedIcon />
              {locationLabel}
            </span>
            <span>
              <RouteOutlinedIcon />
              {formatDistance(match.distanceMiles ?? profile?.distanceMiles ?? null)}
            </span>
          </div>
          {showMatchMade ? (
            <Button
              variant="contained"
              className="matches-select-match matches-select-match--inline matches-select-match--made matches-mobile-detail-counterpart-action"
              disabled
            >
              Match Made
            </Button>
          ) : (
            counterpartReactionBadge && (
              <MatchStatusPill
                tone={counterpartReactionBadge.tone}
                label={counterpartReactionBadge.label}
                className="matches-mobile-detail-counterpart-reaction"
              />
            )
          )}
        </div>

        <div className="matches-mobile-compatibility-card">
          <div className="matches-mobile-compatibility-score">
            <MatchScoreRing value={match.matchScore} size="large" />
            <span>Smart-Guild Matching Overall Score</span>
          </div>
          <div className="matches-mobile-compatibility-info">
            <span className="matches-mobile-compatibility-title">Compatibility Rating</span>
            <span className="matches-mobile-compatibility-subtitle">
              by ColorWorks Group
            </span>
            <div className="matches-detail-stars">
              {stars.map((state, index) => (
                <img
                  key={`${state}-${index}`}
                  src={RATE_ICON_SRC[state]}
                  alt={`${state} star`}
                  className={`rating-star is-${state}`}
                  loading="lazy"
                />
              ))}
            </div>
            <span className="matches-detail-rating-text">
              {compatibilityRating
                ? `(${compatibilityRating.toFixed(1)})${
                    compatibilityLabel ? ` ${compatibilityLabel}` : ""
                  }`
                : ""}
            </span>
            {showLearnMore && onLearnMore && (
              <button
                type="button"
                className="matches-mobile-learn-more"
                onClick={onLearnMore}
              >
                Learn More
              </button>
            )}
          </div>
        </div>

        <div className="matches-mobile-attributes">
          <h3>Top Matching Attributes</h3>
          <div className="matches-chip-row">
            {topTags.length ? (
              topTags.slice(0, 3).map((tag) => (
                <span key={tag} className="matches-chip">
                  {tag}
                </span>
              ))
            ) : (
              <span className="matches-chip matches-chip-muted">No tags yet</span>
            )}
          </div>
        </div>

        <div className="matches-mobile-actions">
          <ReactionButtons
            current={match.reaction ?? null}
            onChange={onReactionChange}
            disabled={Boolean(
              disableInteractions ||
              match.isMatchMade ||
                (snapshot as Record<string, unknown> | null | undefined)?.isOffMarket
            )}
            isMutualLove={Boolean(match.isMutualLove)}
          />
        </div>

        {showViewProfile && (
          <button
            type="button"
            className="matches-mobile-view-profile"
            onClick={onViewProfile}
          >
            View Full Profile
          </button>
        )}
      </div>
    </div>
  );
};
